import React, { useState, useEffect } from 'react';
import { X, AlertCircle, AlertTriangle, Info, CheckCircle, ArrowRight, ChevronRight } from 'lucide-react';
import { Miembro, Lumisial, supabase } from '../lib/supabase';
import { miembroService } from '../services/miembroService';
import { validarDocumento, inferirTipoDocumento, type TipoDocumento } from '../utils/rutValidator';
import { PhoneInput } from './ui/PhoneInput';
import { validarTelefono } from '../utils/phone';
import { validarEmail } from '../utils/email';
import { validarFechaNacimiento, validarMontoEntero } from '../utils/validaciones';
import { Combobox, ComboboxOption } from './ui/Combobox';
import { provinciaEclesiasticaService, ProvinciaEclesiastica } from '../services/provinciaEclesiasticaService';
import { diocesisService, Diocesis } from '../services/diocesisService';
import { lumisialService, type LumisialDestinoTraslado } from '../services/lumisialService';
import { trasladoMiembroService } from '../services/trasladoMiembroService';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { reportarErrorInesperado, detalleTecnico } from '../utils/reportarErrorInesperado';
import { useAuth } from '../context/AuthContext';
import { DialogShell } from './ui/DialogShell';
import type { LocationSelection } from '../types/location';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { calcularEdad, tipoMiembroPorEdad } from '../utils/edad';
import { extraerBase64DeDataUri } from '../utils/dataUri';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import {
  FORM_BORDER,
  FORM_BORDER_ERROR,
  FORM_BORDER_HOVER,
  FOCUS_RING,
  FOCUS_RING_ERROR,
  FORM_RADIUS,
  LABEL_TEXT,
} from './ui/tokens';
import { MiembroFieldsIdentidad, type IdentidadValues } from './miembro/MiembroFieldsIdentidad';
import { MiembroFieldsContacto, type ContactoValues } from './miembro/MiembroFieldsContacto';
import { MiembroFieldsAptitudes, type AptitudesValues } from './miembro/MiembroFieldsAptitudes';
import { MiembroFieldsEmergencia, type EmergenciaValues } from './miembro/MiembroFieldsEmergencia';
import { MiembroFieldsAportes, type AportesValues } from './miembro/MiembroFieldsAportes';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface MiembroModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly miembro?: Miembro;
  readonly lumisialId: string;
  readonly mode: 'create' | 'edit';
}

const MODO_PAGO_OPTIONS = [
  { value: 'Mensual a JAL', label: 'Mensual a JAL' },
  { value: 'Mensual con PA', label: 'Mensual con PA (Pago Automático)' },
  { value: 'Anual a JAL', label: 'Anual a JAL' },
  { value: 'Anual con PA', label: 'Anual con PA (Pago Automático)' },
];

const dateInputClass = (isEmpty: boolean) =>
  `w-full px-4 py-3 ${FORM_RADIUS} ${FOCUS_RING} ${
    isEmpty ? 'border border-amber-300 bg-amber-50' : `${FORM_BORDER} ${FORM_BORDER_HOVER}`
  } bg-white`;

// Normaliza un texto opcional: recortado si tiene contenido, `undefined` si queda vacío.
const textoOpcional = (s?: string | null): string | undefined => {
  const t = s?.trim();
  return t ? t : undefined;
};

const EMPTY_FORM_DATA = {
  nombres: '',
  apellidos: '',
  genero: '',
  foto_data: '',
  rutPasaporte: '',
  fechaNacimiento: '',
  email: '',
  emailConfirm: '',
  telefono: '',
  direccion: '',
  actividadProfesion: '',
  habilidadTalento: '',
  aporteMensual: '',
  aporteCuotaIglesia: '',
  modoPagoCuota: '',
  nombreContactoEmergencia: '',
  numeroContactoEmergencia: '',
  fechaIngreso: '',
  observacionesMiembro: ''
};

// Reconstruye el formData tal como lo carga cargarMiembroEnFormulario, para usarlo
// como línea base de comparación en hasFormChanges (edit mode).
const construirFormDataDesdeMiembro = (m: Miembro) => ({
  nombres: m.nombres,
  apellidos: m.apellidos,
  genero: m.genero ?? '',
  foto_data: '',
  rutPasaporte: m.rut_pasaporte || '',
  fechaNacimiento: m.fecha_nacimiento || '',
  email: m.email || '',
  emailConfirm: m.email || '',
  telefono: m.telefono || '',
  direccion: m.direccion || '',
  actividadProfesion: m.actividad_profesion || '',
  habilidadTalento: m.habilidad_talento || '',
  aporteMensual: String(m.aporte_mensual),
  aporteCuotaIglesia: String(m.aporte_cuota_iglesia),
  modoPagoCuota: m.modo_pago_cuota_iglesia || '',
  nombreContactoEmergencia: m.nombre_contacto_emergencia || '',
  numeroContactoEmergencia: m.numero_contacto_emergencia || '',
  fechaIngreso: m.fecha_ingreso || '',
  observacionesMiembro: m.observaciones_miembro || ''
});

// Errores que el usuario puede resolver solo, corrigiendo el formulario: se muestran
// inline. Devuelve null si el fallo no es corregible por él (va al toast reportable).
const mensajeCorregiblePorUsuario = (e: unknown): string | null => {
  const regla = mensajeErrorDeNegocio(e);
  if (regla) return regla;
  const codigo = e && typeof e === 'object' && 'code' in e ? e.code : null;
  if (codigo === 'PGRST116' || codigo === '42501') {
    return 'No tienes permisos para editar la ficha de este miembro';
  }
  const detalle = detalleTecnico(e);
  if (detalle.includes('modo_pago_cuota_iglesia_check')) {
    return 'El modo de pago seleccionado no es válido. Por favor selecciona una opción válida del menú.';
  }
  if (detalle.includes('duplicate') || detalle.includes('unique')) {
    return 'Ya existe un miembro con este RUT/Pasaporte.';
  }
  return null;
};

// Mensaje del toast cuando el guardado falla por algo ajeno al usuario. Nombra el
// lumisial en el caso de permisos: si vuelve a ocurrir, el reporte dice sobre cuál.
const mensajeGuardadoInesperado = (e: unknown, nombreLumisial?: string): string => {
  const detalle = detalleTecnico(e);
  if (detalle.includes('42501') || detalle.includes('row-level security')) {
    const donde = nombreLumisial ? ` en ${nombreLumisial}` : '';
    return `No pudimos guardar: tu cuenta no tiene permisos para registrar miembros${donde}.`;
  }
  return 'No pudimos guardar el miembro.';
};

function validarEmailCampos(mode: 'create' | 'edit', email: string, emailConfirm: string): string | null {
  if (mode === 'create' && !email.trim()) return 'El correo es obligatorio para enviar el link.';
  if (email.trim() && validarEmail(email, true)) return 'El formato del email no es válido. Por favor verifica que sea correcto.';
  if (email.trim() && email !== emailConfirm) return 'Los emails no coinciden. Por favor verifica que sean iguales.';
  return null;
}

function validarCamposEdicion(
  formData: { rutPasaporte: string; numeroContactoEmergencia: string; fechaNacimiento: string; aporteMensual: string; aporteCuotaIglesia: string; modoPagoCuota: string; },
  tipoDoc: TipoDocumento
): string | null {
  const docError = validarDocumento(formData.rutPasaporte, tipoDoc);
  if (docError) return docError;
  const emergError = validarTelefono(formData.numeroContactoEmergencia, false);
  if (emergError) return 'El teléfono de emergencia no es válido para el país seleccionado.';
  const fechaError = validarFechaNacimiento(formData.fechaNacimiento);
  if (fechaError) return fechaError;
  const aporteError = validarMontoEntero(formData.aporteMensual);
  if (aporteError) return aporteError;
  const cuotaError = validarMontoEntero(formData.aporteCuotaIglesia);
  if (cuotaError) return cuotaError;
  const cuotaIglesia = Number.parseFloat(formData.aporteCuotaIglesia) || 0;
  if (cuotaIglesia > 0 && !formData.modoPagoCuota.trim()) return 'Debe seleccionar un modo de pago para la cuota iglesia cuando hay un monto mayor a $0';
  return null;
}

interface TrasladoInfo {
  readonly hasTraslado: boolean;
  readonly borderClass: string;
  readonly hoverClass: string;
  readonly chevronClass: string;
  readonly editLabel: string;
  readonly editSubmittingLabel: string;
}

function derivarInfoTraslado(
  lumisialDestinoId: string,
  lumisialActualId: string | undefined,
  trasladoExpanded: boolean
): TrasladoInfo {
  const hasTraslado = Boolean(lumisialDestinoId && lumisialDestinoId !== lumisialActualId);
  let borderClass: string;
  if (trasladoExpanded) { borderClass = 'border-2 border-blue-200 bg-blue-50/50'; }
  else if (hasTraslado) { borderClass = 'border-2 border-orange-200 bg-orange-50/30'; }
  else { borderClass = 'border-2 border-slate-200 bg-slate-50'; }
  let hoverClass: string;
  if (trasladoExpanded) { hoverClass = 'hover:bg-blue-100/50'; }
  else if (hasTraslado) { hoverClass = 'hover:bg-orange-100/50'; }
  else { hoverClass = 'hover:bg-slate-100'; }
  let chevronClass: string;
  if (trasladoExpanded) { chevronClass = 'text-blue-600'; }
  else if (hasTraslado) { chevronClass = 'text-orange-600'; }
  else { chevronClass = 'text-slate-600'; }
  return {
    hasTraslado,
    borderClass,
    hoverClass,
    chevronClass,
    editLabel: hasTraslado ? 'Guardar y Trasladar' : 'Guardar',
    editSubmittingLabel: hasTraslado ? 'Trasladando...' : 'Guardando...',
  };
}

export const MiembroModal: React.FC<MiembroModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  miembro,
  lumisialId,
  mode
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tipoDoc, setTipoDoc] = useState<TipoDocumento>('rut');
  const formEndRef = React.useRef<HTMLDivElement>(null);
  const fichaGuardadaRef = React.useRef<string | null>(null);
  const fotoGuardadaRef = React.useRef<string | null>(null);
  // Id de la ficha ya creada en este alta. Si una etapa posterior falla y la persona vuelve a
  // pulsar Guardar, se reintenta solo lo que faltó en vez de crear una segunda ficha.
  const miembroCreadoRef = React.useRef<string | null>(null);
  const guardandoRef = React.useRef(false);

  const { error, clearError, handleError } = useErrorHandler();
  const { usuario } = useAuth();
  const esAdmin = ['admin_global', 'admin_nacional', 'admin_diocesano', 'admin_local']
    .includes(usuario?.rol?.nombre ?? '');

  const [provincias, setProvincias] = useState<ProvinciaEclesiastica[]>([]);
  const [loadingProvincias, setLoadingProvincias] = useState(false);

  const [provinciaDestinoId, setProvinciaDestinoId] = useState('');
  const [diocesisDestinoId, setDiocesisDestinoId] = useState('');
  const [lumisialDestinoId, setLumisialDestinoId] = useState('');
  const [motivoTraslado, setMotivoTraslado] = useState('');
  const [diocesisDestino, setDiocesisDestino] = useState<Diocesis[]>([]);
  const [lumisialesDestino, setLumisialesDestino] = useState<LumisialDestinoTraslado[]>([]);
  const [loadingDiocesisDestino, setLoadingDiocesisDestino] = useState(false);
  const [loadingLumisialesDestino, setLoadingLumisialesDestino] = useState(false);
  const [lumisialActual, setLumisialActual] = useState<Lumisial | null>(null);
  const [trasladoExpanded, setTrasladoExpanded] = useState(false);

  const [formData, setFormData] = useState({
    nombres: '',
    apellidos: '',
    genero: '',
    foto_data: '',
    rutPasaporte: '',
    fechaNacimiento: '',
    email: '',
    emailConfirm: '',
    telefono: '',
    direccion: '',
    actividadProfesion: '',
    habilidadTalento: '',
    aporteMensual: '',
    aporteCuotaIglesia: '',
    modoPagoCuota: '',
    nombreContactoEmergencia: '',
    numeroContactoEmergencia: '',
    fechaIngreso: '',
    observacionesMiembro: ''
  });

  const [residenceLocation, setResidenceLocation] = useState<LocationSelection>({
    country: null,
    state: null,
    city: null
  });

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setErrors({});
  }

  const resetForm = () => {
    fichaGuardadaRef.current = null;
    fotoGuardadaRef.current = null;
    miembroCreadoRef.current = null;
    setFormData(EMPTY_FORM_DATA);
    setResidenceLocation({ country: null, state: null, city: null });
    setProvinciaDestinoId('');
    setDiocesisDestinoId('');
    setLumisialDestinoId('');
    setMotivoTraslado('');
    setDiocesisDestino([]);
    setLumisialesDestino([]);
    setLumisialActual(null);
    setTrasladoExpanded(false);
    handleError('');
    setErrors({});
    setTipoDoc('rut');
  };

  const loadLumisialActual = async () => {
    if (!miembro) return;
    try {
      const lumisial = await lumisialService.getById(miembro.lumisial_actual_id);
      setLumisialActual(lumisial);
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos cargar el lumisial actual.');
    }
  };

  const cargarMiembroEnFormulario = (m: Miembro) => {
    fichaGuardadaRef.current = null;
    fotoGuardadaRef.current = null;
    loadLumisialActual();
    setFormData(construirFormDataDesdeMiembro(m));
    setResidenceLocation({ country: null, state: null, city: null });
  };

  const loadProvincias = async () => {
    try {
      setLoadingProvincias(true);
      const data = await provinciaEclesiasticaService.listarActivos();
      setProvincias(data);
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos cargar las provincias eclesiásticas.');
    } finally {
      setLoadingProvincias(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadProvincias();

      if (mode === 'edit' && miembro) {
        cargarMiembroEnFormulario(miembro);
        setTipoDoc(inferirTipoDocumento(miembro.rut_pasaporte || ''));
      } else {
        resetForm();
      }
      handleError('');
    }
    // cargarMiembroEnFormulario, handleError y resetForm son funciones estables del componente;
    // incluirlas causaría re-renders infinitos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, miembro, mode]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Detecta cambios sin guardar comparando contra la línea base (vacía en create,
  // datos del miembro cargado en edit). DialogShell muestra la confirmación
  // "¿cerrar sin guardar?" cuando esto es true.
  const hasFormChanges = (): boolean => {
    const baseline = mode === 'edit' && miembro ? construirFormDataDesdeMiembro(miembro) : EMPTY_FORM_DATA;
    if (JSON.stringify(formData) !== JSON.stringify(baseline)) return true;
    if (residenceLocation.country || residenceLocation.state || residenceLocation.city) return true;
    if (mode === 'edit' && (provinciaDestinoId || diocesisDestinoId || lumisialDestinoId || motivoTraslado.trim())) return true;
    return false;
  };

  const closeRef = React.useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  const loadDiocesisDestino = async (provinciaId: string) => {
    try {
      setLoadingDiocesisDestino(true);
      const data = await diocesisService.getByProvinciaEclesiastica(provinciaId);
      setDiocesisDestino(data);
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos cargar las diócesis de destino.');
      setDiocesisDestino([]);
    } finally {
      setLoadingDiocesisDestino(false);
    }
  };

  const loadLumisialesDestino = async (diocesisId: string) => {
    if (!miembro) return;
    try {
      setLoadingLumisialesDestino(true);
      // Por función dedicada, no por lectura de `lumisiales`: esa lectura está limitada por
      // jurisdicción y una Junta Sacerdotal local solo veía su propio Lumisial (bloqueo B2).
      const data = await lumisialService.obtenerDestinosTraslado(miembro.id, diocesisId);
      setLumisialesDestino(data);
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos cargar los lumisiales de destino.');
      setLumisialesDestino([]);
    } finally {
      setLoadingLumisialesDestino(false);
    }
  };

  const handleProvinciaDestinoChange = (provinciaId: string) => {
    setProvinciaDestinoId(provinciaId);
    setDiocesisDestinoId('');
    setLumisialDestinoId('');
    setDiocesisDestino([]);
    setLumisialesDestino([]);
    if (provinciaId) loadDiocesisDestino(provinciaId);
  };

  const handleDiocesisDestinoChange = (diocesisId: string) => {
    setDiocesisDestinoId(diocesisId);
    setLumisialDestinoId('');
    setLumisialesDestino([]);
    if (diocesisId) loadLumisialesDestino(diocesisId);
  };

  const handleTelefonoChange = (value: string) => setFormData(prev => ({ ...prev, telefono: value }));

  // Error de formato derivado del valor actual (no se guarda en estado: se recalcula al render).
  const telefonoError = validarTelefono(formData.telefono, false) ?? '';

  const validarCampos = (): string | null => {
    if (!formData.nombres.trim()) return 'Los nombres son requeridos';
    if (!formData.apellidos.trim()) return 'Los apellidos son requeridos';
    const emailErr = validarEmailCampos(mode, formData.email, formData.emailConfirm);
    if (emailErr) return emailErr;
    const telError = validarTelefono(formData.telefono, true);
    if (telError) return telError === 'Campo requerido' ? 'El teléfono es obligatorio.' : 'El teléfono no es válido para el país seleccionado.';
    return mode === 'create' ? null : validarCamposEdicion(formData, tipoDoc);
  };

  const construirMiembroData = () => {
    const cuotaIglesia = Number.parseFloat(formData.aporteCuotaIglesia) || 0;
    return {
      nombres: formData.nombres.trim(),
      apellidos: formData.apellidos.trim(),
      rut_pasaporte: textoOpcional(formData.rutPasaporte),
      fecha_nacimiento: textoOpcional(formData.fechaNacimiento),
      email: textoOpcional(formData.email),
      telefono: textoOpcional(formData.telefono),
      direccion: textoOpcional(formData.direccion),
      ciudad: textoOpcional(residenceLocation.city?.name),
      pais_residencia: textoOpcional(residenceLocation.country?.name),
      pais_residencia_iso2: textoOpcional(residenceLocation.country?.iso2),
      estado_residencia: textoOpcional(residenceLocation.state?.name),
      estado_residencia_iso2: textoOpcional(residenceLocation.state?.iso2),
      ciudad_residencia: textoOpcional(residenceLocation.city?.name),
      ciudad_residencia_id: residenceLocation.city?.id ?? undefined,
      actividad_profesion: textoOpcional(formData.actividadProfesion),
      habilidad_talento: textoOpcional(formData.habilidadTalento),
      tipo_miembro: tipoMiembroPorEdad(formData.fechaNacimiento) ?? 'adulto',
      aporte_mensual: Number.parseFloat(formData.aporteMensual) || 0,
      aporte_cuota_iglesia: cuotaIglesia,
      modo_pago_cuota_iglesia: textoOpcional(formData.modoPagoCuota),
      nombre_contacto_emergencia: textoOpcional(formData.nombreContactoEmergencia),
      numero_contacto_emergencia: textoOpcional(formData.numeroContactoEmergencia),
      fecha_ingreso: textoOpcional(formData.fechaIngreso),
      ...(esAdmin ? { observaciones_miembro: formData.observacionesMiembro.trim() || null } : {}),
      genero: (formData.genero as 'Dama' | 'Caballero') || undefined,
    };
  };

  const scrollAlAvisoDeError = () => {
    formEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  // Rutea el fallo de guardado: lo corregible se muestra en el formulario, el resto va
  // al toast reportable. Vive fuera de handleSubmit para no cargarle complejidad.
  const manejarErrorDeGuardado = (e: unknown) => {
    const corregible = mensajeCorregiblePorUsuario(e);
    if (corregible) {
      handleError(corregible);
      scrollAlAvisoDeError();
      return;
    }
    reportarErrorInesperado(e, mensajeGuardadoInesperado(e, lumisialActual?.nombre), {
      modo: mode,
      lumisial_id: lumisialId,
    });
  };

  const subirFoto = async (miembroId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    const respuesta = await fetch('/api/foto-miembro', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`
      },
      body: JSON.stringify({ miembro_id: miembroId, foto_data: formData.foto_data })
    });
    // `fetch` no lanza ante un 4xx: sin esta guarda, una foto que el servidor rechazó se daba
    // por guardada y el formulario mostraba éxito igual.
    if (!respuesta.ok) throw new Error(`El servidor rechazó la fotografía (HTTP ${respuesta.status}).`);
  };

  // Crea la ficha solo la primera vez. Si una etapa posterior falla y la persona vuelve a pulsar
  // Guardar, se reutiliza la ficha que ya existe en vez de dar de alta a la misma persona dos veces.
  const crearFichaSiFalta = async (): Promise<string> => {
    if (!miembroCreadoRef.current) {
      const datos = { ...construirMiembroData(), lumisial_actual_id: lumisialId };
      const creado = await miembroService.create(datos as unknown as Omit<Miembro, 'id' | 'created_at'>);
      miembroCreadoRef.current = creado.id;
    }
    return miembroCreadoRef.current;
  };

  const guardarEdicion = async (): Promise<void> => {
    if (!miembro) throw new Error('No se encontró el miembro');
    const datos = construirMiembroData();
    const firma = JSON.stringify(datos);
    if (fichaGuardadaRef.current !== firma) {
      await miembroService.update(miembro.id, datos);
      fichaGuardadaRef.current = firma;
    }
  };

  // La foto es la etapa menos importante del guardado, así que va última y se trata igual que el
  // traslado: si falla, lo demás ya ocurrió — se avisa y se deja la ventana abierta para reintentar,
  // en vez de lanzar y presentar como fallo total algo que sí se guardó.
  const intentarGuardarFoto = async (miembroId: string): Promise<boolean> => {
    if (!formData.foto_data || fotoGuardadaRef.current === formData.foto_data) return true;
    try {
      await subirFoto(miembroId);
      fotoGuardadaRef.current = formData.foto_data;
      return true;
    } catch (error: unknown) {
      handleError('Los datos se guardaron, pero la fotografía no se actualizó. Puedes intentarlo de nuevo.');
      reportarErrorInesperado(error, 'No pudimos guardar la fotografía.', {
        modo: mode,
        lumisial_id: lumisialId,
      });
      scrollAlAvisoDeError();
      return false;
    }
  };

  const intentarTraslado = async (): Promise<boolean> => {
    if (!miembro || !esAdmin || !lumisialDestinoId || lumisialDestinoId === miembro.lumisial_actual_id) {
      return true;
    }
    try {
      const validacion = await trasladoMiembroService.validarTraslado(miembro.lumisial_actual_id, lumisialDestinoId);
      if (!validacion.valido) throw new Error(validacion.mensaje);
      await trasladoMiembroService.registrarTraslado({
        miembro_id: miembro.id,
        lumisial_destino_id: lumisialDestinoId,
        motivo: motivoTraslado.trim() || undefined,
      });
      return true;
    } catch (error: unknown) {
      const regla = mensajeErrorDeNegocio(error);
      const motivo = regla ?? 'No pudimos completar el traslado. Inténtalo nuevamente.';
      handleError(`Los cambios de la ficha se guardaron, pero el traslado no se realizó: ${motivo}`);
      if (!regla) reportarErrorInesperado(error, 'No pudimos completar el traslado.');
      scrollAlAvisoDeError();
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (guardandoRef.current) return;

    // El campo de confirmación ya muestra "Los emails no coinciden" bajo sí mismo: repetirlo en el
    // aviso del final sería decir dos veces lo mismo. Se lleva la vista al campo y basta.
    if (errors.email_confirm) {
      handleError('');
      document.getElementById('emailConfirm-create')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const validationError = validarCampos();
    if (validationError) {
      handleError(validationError);
      scrollAlAvisoDeError();
      return;
    }

    try {
      guardandoRef.current = true;
      setSubmitting(true);
      handleError('');
      if (mode === 'create') {
        const miembroId = await crearFichaSiFalta();
        if (!await intentarGuardarFoto(miembroId)) return;
      } else {
        await guardarEdicion();
        if (!miembro) throw new Error('No se encontró el miembro');
        if (!await intentarTraslado()) return;
        if (!await intentarGuardarFoto(miembro.id)) return;
      }
      resetForm();
      onSuccess();
      onClose();
    } catch (error: unknown) {
      manejarErrorDeGuardado(error);
    } finally {
      guardandoRef.current = false;
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const provinciaOptions: ComboboxOption[] = provincias.map(p => ({ value: p.id, label: p.nombre }));

  // Estilos del email en modo create (basados en errors dict).
  const getEmailBorderClass = () => {
    if (errors.email) return `${FORM_BORDER_ERROR} ${FOCUS_RING_ERROR}`;
    if (!formData.email.trim()) return `border border-amber-300 ${FOCUS_RING}`;
    return `${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING}`;
  };
  const getEmailConfirmBorderClass = () => {
    if (errors.email_confirm) return `${FORM_BORDER_ERROR} ${FOCUS_RING_ERROR}`;
    if (!errors.email_confirm && formData.emailConfirm.trim() && formData.email === formData.emailConfirm) {
      return `border border-green-400 ${FOCUS_RING}`;
    }
    if (!formData.email.trim() || errors.email !== '') return `${FORM_BORDER} ${FOCUS_RING}`;
    return `${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING}`;
  };
  const emailBorderClass = getEmailBorderClass();
  const emailConfirmBorderClass = getEmailConfirmBorderClass();
  const emailBg = formData.email.trim() ? 'bg-white' : 'bg-amber-50';

  const { hasTraslado: _hasTraslado, borderClass: trasladoBorderClass, hoverClass: trasladoHoverClass, chevronClass: chevronColorClass, editLabel, editSubmittingLabel } = derivarInfoTraslado(esAdmin ? lumisialDestinoId : '', miembro?.lumisial_actual_id, trasladoExpanded);
  const submitLabel = mode === 'create' ? 'Registrar' : editLabel;
  const submittingLabel = mode === 'create' ? 'Registrando...' : editSubmittingLabel;

  // Adaptadores camelCase ↔ snake_case para los grupos en modo edit.
  const identidadValues: IdentidadValues = {
    nombres: formData.nombres, apellidos: formData.apellidos, genero: formData.genero,
    rut_pasaporte: formData.rutPasaporte, fecha_nacimiento: formData.fechaNacimiento,
  };
  const onIdentidad = (f: keyof IdentidadValues, v: string) => {
    const map = {
      nombres: 'nombres', apellidos: 'apellidos', genero: 'genero',
      rut_pasaporte: 'rutPasaporte', fecha_nacimiento: 'fechaNacimiento',
    } as const;
    setFormData(prev => ({ ...prev, [map[f]]: v }));
    if (f === 'rut_pasaporte') {
      setErrors(prev => ({ ...prev, rut_pasaporte: validarDocumento(v, tipoDoc) ?? '' }));
    }
  };

  const contactoValues: ContactoValues = {
    telefono: formData.telefono, email: formData.email,
    email_confirm: formData.emailConfirm, direccion: formData.direccion,
  };
  const onContacto = (f: keyof ContactoValues, v: string) => {
    const map = {
      telefono: 'telefono', email: 'email',
      email_confirm: 'emailConfirm', direccion: 'direccion',
    } as const;
    setFormData(prev => ({ ...prev, [map[f]]: v }));
    if (f === 'email') setErrors(prev => ({ ...prev, email: validarEmail(v, true) ?? '' }));
    if (f === 'email_confirm') {
      setErrors(prev => ({ ...prev, email_confirm: v === formData.email ? '' : 'Los emails no coinciden' }));
    }
  };

  const aptitudesValues: AptitudesValues = {
    actividad_profesion: formData.actividadProfesion,
    habilidad_talento: formData.habilidadTalento,
  };
  const onAptitudes = (f: keyof AptitudesValues, v: string) => {
    const map = { actividad_profesion: 'actividadProfesion', habilidad_talento: 'habilidadTalento' } as const;
    setFormData(prev => ({ ...prev, [map[f]]: v }));
  };

  const emergenciaValues: EmergenciaValues = {
    nombre_contacto_emergencia: formData.nombreContactoEmergencia,
    numero_contacto_emergencia: formData.numeroContactoEmergencia,
  };
  const onEmergencia = (f: keyof EmergenciaValues, v: string) => {
    const map = {
      nombre_contacto_emergencia: 'nombreContactoEmergencia',
      numero_contacto_emergencia: 'numeroContactoEmergencia',
    } as const;
    setFormData(prev => ({ ...prev, [map[f]]: v }));
  };

  const aportesValues: AportesValues = {
    aporte_mensual: formData.aporteMensual,
    aporte_cuota_iglesia: formData.aporteCuotaIglesia,
    modo_pago_cuota_iglesia: formData.modoPagoCuota,
  };
  const onAportes = (f: keyof AportesValues, v: string) => {
    const map = {
      aporte_mensual: 'aporteMensual',
      aporte_cuota_iglesia: 'aporteCuotaIglesia',
      modo_pago_cuota_iglesia: 'modoPagoCuota',
    } as const;
    setFormData(prev => ({ ...prev, [map[f]]: v }));
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={handleClose}
      size="3xl"
      hasUnsavedChanges={submitting || error !== '' || hasFormChanges()}
      closeRef={closeRef}
      header={(
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-slate-800">
            {mode === 'create' ? 'Registrar Nuevo Miembro' : 'Editar Miembro'}
          </h2>
          <button type="button" onClick={requestClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" aria-label="Cerrar">
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>
      )}
    >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* El aviso de error va solo al final del formulario, junto a los botones: es
              donde handleSubmit hace scroll. Antes se pintaba también acá arriba y el
              mismo mensaje aparecía dos veces. */}
          {mode === 'create' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Datos mínimos para enviar el link</p>
                  <p>Solo se necesitan estos datos para crear al miembro y enviarle el link único. El miembro completará el resto de su ficha desde el link.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput
                  id="nombres-create"
                  label="Nombres"
                  value={formData.nombres}
                  onChange={(v) => setFormData(prev => ({ ...prev, nombres: v }))}
                  placeholder="Ej: Juan Pablo"
                  required
                />
                <TextInput
                  id="apellidos-create"
                  label="Apellidos"
                  value={formData.apellidos}
                  onChange={(v) => setFormData(prev => ({ ...prev, apellidos: v }))}
                  placeholder="Ej: González Pérez"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email-create" className={`block ${LABEL_TEXT} mb-1.5`}>
                    Correo<span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    id="email-create"
                    value={formData.email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData(prev => ({ ...prev, email: v }));
                      setErrors(prev => ({ ...prev, email: validarEmail(v, true) ?? '' }));
                      if (formData.emailConfirm.trim() && v !== formData.emailConfirm) {
                        setErrors(prev => ({ ...prev, email_confirm: 'Los emails no coinciden' }));
                      } else {
                        setErrors(prev => ({ ...prev, email_confirm: '' }));
                      }
                    }}
                    className={`w-full px-4 py-3 ${FORM_RADIUS} ${emailBorderClass} ${emailBg}`}
                    placeholder="ejemplo@correo.com"
                  />
                  {errors.email && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />{errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="emailConfirm-create" className={`block ${LABEL_TEXT} mb-1.5`}>
                    Confirmar correo<span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    id="emailConfirm-create"
                    value={formData.emailConfirm}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData(prev => ({ ...prev, emailConfirm: v }));
                      setErrors(prev => ({ ...prev, email_confirm: v.trim() && v !== formData.email ? 'Los emails no coinciden' : '' }));
                    }}
                    disabled={!formData.email.trim() || errors.email !== ''}
                    className={`w-full px-4 py-3 ${FORM_RADIUS} ${emailConfirmBorderClass} ${
                      !formData.email.trim() || errors.email !== '' ? 'bg-slate-100 cursor-not-allowed' : 'bg-white'
                    }`}
                    placeholder={!formData.email.trim() ? 'Ingrese correo primero' : 'Repetir correo'}
                  />
                  {errors.email_confirm && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />{errors.email_confirm}
                    </p>
                  )}
                  {!errors.email_confirm && formData.emailConfirm.trim() && formData.email === formData.emailConfirm && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />Los correos coinciden correctamente
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PhoneInput
                  id="telefono-create"
                  label="Teléfono"
                  value={formData.telefono}
                  onChange={handleTelefonoChange}
                  error={telefonoError}
                  required
                />
                <div>
                  <label htmlFor="fechaIngreso-create" className={`block ${LABEL_TEXT} mb-1.5`}>
                    Fecha de ingreso
                  </label>
                  <input
                    type="date"
                    id="fechaIngreso-create"
                    value={formData.fechaIngreso}
                    onChange={(e) => setFormData(prev => ({ ...prev, fechaIngreso: e.target.value }))}
                    className={`w-full px-4 py-3 ${FORM_RADIUS} ${FORM_BORDER} ${FORM_BORDER_HOVER} ${FOCUS_RING} bg-white`}
                  />
                </div>
              </div>
            </div>
          )}

          {mode === 'edit' && (<>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800">Identidad</h3>
              <MiembroFieldsIdentidad
                values={identidadValues} onFieldChange={onIdentidad} errors={errors}
                tipoDoc={tipoDoc} onTipoDocChange={(t) => setTipoDoc(t)}
                onFotoReady={(uri) => { setFormData(prev => ({ ...prev, foto_data: extraerBase64DeDataUri(uri) })); }}
                onFotoClear={() => setFormData(prev => ({ ...prev, foto_data: '' }))}
                fotoPosition="first" fotoUrl={miembro?.foto_url ?? undefined}
                fechaNacimientoSibling={(
                  <div>
                    <label htmlFor="fechaIngreso" className={`block ${LABEL_TEXT} mb-1.5 flex items-center gap-1`}>
                      Fecha Ingreso
                      {!formData.fechaIngreso.trim() && (
                        <span className="text-xs text-amber-600 font-normal">(recomendado)</span>
                      )}
                    </label>
                    <input
                      type="date"
                      id="fechaIngreso"
                      value={formData.fechaIngreso}
                      onChange={(e) => setFormData(prev => ({ ...prev, fechaIngreso: e.target.value }))}
                      className={dateInputClass(!formData.fechaIngreso.trim())}
                    />
                    {!formData.fechaIngreso.trim() && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />Campo recomendado para registro histórico
                      </p>
                    )}
                  </div>
                )} />

              {(() => {
                const edad = calcularEdad(formData.fechaNacimiento);
                if (edad === null) {
                  return <p className="text-sm text-slate-500">Ing. fe. de nac.</p>;
                }
                return (
                  <p className="text-base text-slate-800">
                    <span className="font-semibold">{edad >= 18 ? 'Adulto' : 'Niño'}</span>{' '}
                    <span className="text-slate-500">({edad} {edad === 1 ? 'año' : 'años'})</span>
                  </p>
                );
              })()}
            </div>

            <div className="space-y-4 pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Contacto</h3>
              <MiembroFieldsContacto
                values={contactoValues} onFieldChange={onContacto} errors={errors}
                ubicacion={residenceLocation} onUbicacionChange={setResidenceLocation}
                />
            </div>

            <div className="space-y-4 pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Perfil y aptitudes</h3>
              <MiembroFieldsAptitudes values={aptitudesValues} onFieldChange={onAptitudes} errors={errors} />
            </div>

            <div className="space-y-4 pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Contacto de emergencia</h3>
              <MiembroFieldsEmergencia values={emergenciaValues} onFieldChange={onEmergencia} errors={errors} />
            </div>

            <div className="space-y-4 pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Compromiso económico</h3>
              <MiembroFieldsAportes
                values={aportesValues} onFieldChange={onAportes} errors={errors}
                modoPagoOptions={MODO_PAGO_OPTIONS}
                aporteMensualLabel="Aporte Lumisial ($)"
                cuotaLabel="Cuota Iglesia ($5.000 sugerido)" />
            </div>

            {esAdmin && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800">Observaciones</h3>
                <Textarea
                  id="observaciones_miembro"
                  label="Observaciones"
                  value={formData.observacionesMiembro}
                  onChange={(e) => { setFormData(prev => ({ ...prev, observacionesMiembro: e.target.value })); }}
                  rows={4}
                  placeholder="Agregar observaciones o información adicional sobre el miembro..."
                />
              </div>
            )}

            {esAdmin && lumisialActual && (
              <div className={`rounded-xl overflow-hidden transition-all ${trasladoBorderClass}`}>
                <button
                  type="button"
                  onClick={() => setTrasladoExpanded(!trasladoExpanded)}
                  className={`w-full p-4 flex items-center justify-between transition-colors ${trasladoHoverClass}`}
                >
                  <div className="flex items-center gap-3">
                    <ChevronRight className={`w-5 h-5 transition-transform ${trasladoExpanded ? 'rotate-90' : ''} ${chevronColorClass}`} />
                    <h3 className="text-lg font-semibold text-slate-800">Traslado de Lumisial</h3>
                  </div>
                  {!trasladoExpanded && lumisialDestinoId && lumisialDestinoId !== miembro?.lumisial_actual_id && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300">
                      <ArrowRight className="w-3 h-3 mr-1" />Cambio por Guardar
                    </span>
                  )}
                </button>

                {trasladoExpanded && (
                  <div className="px-6 pb-6 space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <p className="text-sm text-slate-600 mb-2">Lumisial Actual:</p>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-blue-100 text-blue-800">
                          {lumisialActual.nombre}
                        </span>
                        <span className="text-xs text-slate-500">{lumisialActual.ciudad}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Combobox
                        label="Provincia Eclesiástica (País)"
                        options={provinciaOptions}
                        value={provinciaDestinoId}
                        onChange={handleProvinciaDestinoChange}
                        placeholder="Seleccionar provincia..."
                        disabled={loadingProvincias}
                        emptyMessage="No hay provincias disponibles"
                      />
                      <Combobox
                        label="Diócesis"
                        options={diocesisDestino.map(d => ({ value: d.id, label: d.nombre }))}
                        value={diocesisDestinoId}
                        onChange={handleDiocesisDestinoChange}
                        placeholder={provinciaDestinoId ? 'Seleccionar diócesis...' : 'Seleccione provincia primero'}
                        disabled={!provinciaDestinoId || loadingDiocesisDestino}
                        emptyMessage="No hay diócesis disponibles"
                      />
                      <Combobox
                        label="Lumisial Destino"
                        options={lumisialesDestino.map(l => ({ value: l.id, label: l.nombre, subtitle: l.ciudad }))}
                        value={lumisialDestinoId}
                        onChange={setLumisialDestinoId}
                        placeholder={diocesisDestinoId ? 'Seleccionar lumisial...' : 'Seleccione diócesis primero'}
                        disabled={!diocesisDestinoId || loadingLumisialesDestino}
                        emptyMessage="No hay lumisiales disponibles"
                      />
                    </div>

                    <div>
                      <Textarea
                        id="motivoTraslado"
                        label="Motivo del Traslado (opcional)"
                        value={motivoTraslado}
                        onChange={(e) => setMotivoTraslado(e.target.value)}
                        rows={2}
                        maxLength={500}
                        placeholder="Ej: Cambio de residencia, traslado laboral, etc."
                        disabled={!lumisialDestinoId}
                      />
                      <p className="text-xs text-slate-500 mt-1">{motivoTraslado.length}/500 caracteres</p>
                    </div>

                    {lumisialDestinoId && lumisialDestinoId !== miembro?.lumisial_actual_id && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
                        <Info className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-orange-800">
                          Al guardar, el miembro será trasladado al lumisial seleccionado y se registrará en el historial de traslados.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>)}

          <div ref={formEndRef} className="space-y-4">
            <FormErrorAlert error={error} onClear={clearError} />
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={requestClose}
                className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? submittingLabel : submitLabel}
              </button>
            </div>
          </div>
        </form>
    </DialogShell>
  );
};
